import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebase-config';
import { collection, addDoc, onSnapshot, doc, updateDoc, writeBatch, query, where, getDocs, serverTimestamp, orderBy } from 'firebase/firestore';
import { CheckCircle, Edit, Save, List } from 'lucide-react';

const KioskSettingsView = ({ user }) => {
    const [promptContent, setPromptContent] = useState('');
    const [promptHistory, setPromptHistory] = useState([]);
    const [activePromptId, setActivePromptId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        const promptsCollectionRef = collection(db, 'kiosk_prompts');
        const q = query(promptsCollectionRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const prompts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPromptHistory(prompts);

            const activePrompt = prompts.find(p => p.isActive);
            if (activePrompt) {
                setActivePromptId(activePrompt.id);
                // Si el área de texto está vacía, la llenamos con el prompt activo
                if (promptContent === '') {
                    setPromptContent(activePrompt.content);
                }
            }
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching prompts:", err);
            setError("No se pudo cargar el historial de prompts.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, promptContent]); // Dependemos de promptContent para recargar el prompt activo si se limpia

    const handleSaveAndActivate = async () => {
        if (!promptContent.trim()) {
            setError("El prompt no puede estar vacío.");
            return;
        }

        setIsSaving(true);
        setError('');
        setSaveSuccess(false);

        try {
            const promptsCollectionRef = collection(db, 'kiosk_prompts');
            
            // 1. Buscamos si ya hay un prompt activo para desactivarlo
            const q = query(promptsCollectionRef, where("isActive", "==", true));
            const activeDocs = await getDocs(q);
            
            const batch = writeBatch(db);

            // 2. Desactivamos el prompt activo anterior
            activeDocs.forEach(document => {
                const docRef = doc(db, 'kiosk_prompts', document.id);
                batch.update(docRef, { isActive: false });
            });

            // 3. Añadimos el nuevo prompt y lo marcamos como activo
            const newPromptRef = doc(collection(db, 'kiosk_prompts')); // Creamos referencia para obtener ID
            batch.set(newPromptRef, {
                content: promptContent,
                isActive: true,
                createdAt: serverTimestamp(),
                createdBy: user.email,
            });
            
            // 4. Ejecutamos todas las operaciones
            await batch.commit();

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);

        } catch (err) {
            console.error("Error saving prompt:", err);
            setError("Ocurrió un error al guardar el prompt.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleActivatePrompt = async (promptToActivateId) => {
        setIsSaving(true);
        try {
            const promptsCollectionRef = collection(db, 'kiosk_prompts');
            const q = query(promptsCollectionRef, where("isActive", "==", true));
            const activeDocs = await getDocs(q);
            
            const batch = writeBatch(db);

            activeDocs.forEach(document => {
                batch.update(doc(db, 'kiosk_prompts', document.id), { isActive: false });
            });

            const newActiveRef = doc(db, 'kiosk_prompts', promptToActivateId);
            batch.update(newActiveRef, { isActive: true });

            await batch.commit();
            
            const selectedPrompt = promptHistory.find(p => p.id === promptToActivateId);
            if(selectedPrompt) {
                setPromptContent(selectedPrompt.content);
            }

        } catch (err) {
            console.error("Error activating prompt:", err);
            setError("No se pudo activar el prompt seleccionado.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Editor de Prompt del Kiosk</h2>
                <p className="text-gray-600 mb-4 text-sm">
                    Aquí defines las instrucciones, personalidad y objetivos de Cartonbot. El prompt que guardes y actives será el que use el Kiosk en tiempo real.
                </p>

                <textarea
                    className="w-full h-96 p-3 border border-gray-300 rounded-md font-mono text-sm shadow-inner"
                    value={promptContent}
                    onChange={(e) => setPromptContent(e.target.value)}
                    placeholder="Escribe aquí las instrucciones para Cartonbot..."
                />
                
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSaveAndActivate}
                        disabled={isSaving || isLoading}
                        className={`flex items-center gap-2 px-6 py-2 font-bold text-white rounded-lg transition-colors ${
                            isSaving ? 'bg-gray-400 cursor-not-allowed' : saveSuccess ? 'bg-green-500' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {isSaving ? 'Guardando...' : saveSuccess ? <><CheckCircle size={20} /> ¡Activado!</> : <><Save size={20}/> Guardar y Activar</>}
                    </button>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <List size={22} /> Historial de Prompts
                </h3>
                {isLoading ? (
                    <p>Cargando historial...</p>
                ) : (
                    <ul className="space-y-3 max-h-[500px] overflow-y-auto">
                        {promptHistory.map(p => (
                            <li key={p.id} className={`p-3 rounded-lg border-2 transition-all ${p.id === activePromptId ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                                <p className="text-xs text-gray-500 mb-2">
                                    Creado el: {p.createdAt?.toDate().toLocaleDateString()} por {p.createdBy}
                                </p>
                                <p className="text-sm text-gray-700 line-clamp-3 mb-3">
                                    {p.content}
                                </p>
                                {p.id === activePromptId ? (
                                    <div className="flex items-center gap-2 text-green-600 font-bold">
                                        <CheckCircle size={16} /> Activo
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => handleActivatePrompt(p.id)}
                                        disabled={isSaving}
                                        className="w-full px-3 py-1.5 text-sm font-semibold text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:bg-gray-400"
                                    >
                                        Activar este Prompt
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default KioskSettingsView;

